import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Instagram, Facebook, Music2, Phone, Mail, MapPin, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });
const inputCls =
  "w-full rounded-xl bg-white border border-black/10 px-4 py-2.5 text-sm focus:border-[#c9006f] transition-colors";
const labelCls = "block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#121212]/45 mb-1.5";

export const SettingsTab = ({ token, refreshContent }) => {
  const [form, setForm] = useState({ instagram_url: "", facebook_url: "", tiktok_url: "", phone: "", business_email: "", address: "", hours: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content/settings`).then((r) => setForm(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/settings`, form, auth(token));
      toast.success("Settings saved");
      refreshContent();
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("gb_admin_token");
        window.location.reload();
      }
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl space-y-5" data-testid="admin-settings-tab">
      <div className="bg-white rounded-2xl border border-black/5 p-6 shadow-sm space-y-4">
        <p className="font-serif text-xl text-[#121212]">Social Links</p>
        <p className="text-sm text-[#121212]/50">
          These icons appear in the website footer. Leave a field empty to hide that icon.
        </p>
        <div>
          <span className={labelCls}><Instagram size={11} className="inline mr-1" /> Instagram URL</span>
          <input
            className={inputCls}
            value={form.instagram_url}
            onChange={(e) => setForm({ ...form, instagram_url: e.target.value })}
            placeholder="https://www.instagram.com/glambooth.cayman/"
            data-testid="admin-settings-instagram-input"
          />
        </div>
        <div>
          <span className={labelCls}><Facebook size={11} className="inline mr-1" /> Facebook URL</span>
          <input
            className={inputCls}
            value={form.facebook_url}
            onChange={(e) => setForm({ ...form, facebook_url: e.target.value })}
            placeholder="https://www.facebook.com/yourpage (empty = hidden)"
            data-testid="admin-settings-facebook-input"
          />
        </div>
        <div>
          <span className={labelCls}><Music2 size={11} className="inline mr-1" /> TikTok URL</span>
          <input
            className={inputCls}
            value={form.tiktok_url}
            onChange={(e) => setForm({ ...form, tiktok_url: e.target.value })}
            placeholder="https://www.tiktok.com/@yourhandle (empty = hidden)"
            data-testid="admin-settings-tiktok-input"
          />
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-black/5 p-6 shadow-sm space-y-4">
        <p className="font-serif text-xl text-[#121212]">Contact Details</p>
        <p className="text-sm text-[#121212]/50">
          Shown across the website (booking section, footer, about page). WhatsApp and call links update automatically from the phone number.
        </p>
        <div>
          <span className={labelCls}><Phone size={11} className="inline mr-1" /> Phone Number</span>
          <input
            className={inputCls}
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="+1 (345) 328-6172"
            data-testid="admin-settings-phone-input"
          />
        </div>
        <div>
          <span className={labelCls}><Mail size={11} className="inline mr-1" /> Business Email</span>
          <input
            className={inputCls}
            value={form.business_email}
            onChange={(e) => setForm({ ...form, business_email: e.target.value })}
            placeholder="info@glamboothcayman.com"
            data-testid="admin-settings-email-input"
          />
        </div>
        <div>
          <span className={labelCls}><MapPin size={11} className="inline mr-1" /> Address</span>
          <input
            className={inputCls}
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            placeholder="80 Shedden Road, George Town, Grand Cayman"
            data-testid="admin-settings-address-input"
          />
          <p className="text-[11px] text-[#121212]/40 mt-1">Format: Street, Town, Island — also used for your Google listing info.</p>
        </div>
        <div>
          <span className={labelCls}><Clock size={11} className="inline mr-1" /> Business Hours</span>
          <input
            className={inputCls}
            value={form.hours}
            onChange={(e) => setForm({ ...form, hours: e.target.value })}
            placeholder="Mon–Sun · 9:00 AM – 9:00 PM"
            data-testid="admin-settings-hours-input"
          />
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="admin-settings-save-btn"
          className="btn-gold rounded-full px-8 py-2.5 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Settings
        </button>
      </div>
    </div>
  );
};
